import { useState, useEffect, useCallback, useRef } from "react";
import { Wallet, Loader2, Plus, Trash2, TrendingDown, Paperclip, FileText, Sparkles, Download, FolderArchive } from "lucide-react";
import { apiFetch, uploadFile, openPdf, downloadFile } from "@/portal/api";
import { fmtCZK } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";
import DateInput from "@/portal/components/DateInput";
import BulkExpenseModal from "@/portal/components/BulkExpenseModal";

const CATS = [
  { key: "service", label: "Servis/opravy" },
  { key: "insurance", label: "Pojištění" },
  { key: "fuel", label: "Palivo" },
  { key: "purchase", label: "Nákup vozu" },
  { key: "marketing", label: "Marketing" },
  { key: "other", label: "Ostatní" },
];
const catLabel = (k) => (CATS.find((c) => c.key === k) || CATS[5]).label;
const today = () => new Date().toISOString().slice(0, 10);
const fmtD = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric" }) : "");
const monthKey = (s) => (s ? s.slice(0, 7) : "");

const ExpensesTab = ({ cars = [], token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: today(), category: "service", amount: "", currency: "CZK", note: "", car_id: "", supplier: "", invoice_number: "" });
  const [saving, setSaving] = useState(false);
  const [rates, setRates] = useState(null);
  const [attachFile, setAttachFile] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [zipMonth, setZipMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [zipBusy, setZipBusy] = useState(false);
  const rowFileRef = useRef(null);
  const pendingRowId = useRef(null);

  const load = useCallback(async () => {
    try { setItems(await apiFetch("/admin/expenses", { token })); } catch (e) { /* */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (form.currency === "CZK") return;
    const d = form.date;
    const t = setTimeout(() => {
      apiFetch(`/admin/fx/rates?date=${encodeURIComponent(d)}`, { token }).then((r) => setRates(r)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [form.currency, form.date, token]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Zadejte částku."); return; }
    setSaving(true);
    try {
      const body = { date: form.date, category: form.category, amount: Number(form.amount), currency: form.currency, note: form.note || null, car_id: form.car_id || null, supplier: form.supplier || null, invoice_number: form.invoice_number || null };
      const created = await apiFetch("/admin/expenses", { method: "POST", token, body });
      if (attachFile && created?.id) {
        try { await uploadFile(`/admin/expenses/${created.id}/attachment`, attachFile, token); }
        catch (err) { toast.error(`Náklad uložen, ale příloha selhala: ${err.message}`); }
      }
      toast.success("Náklad přidán."); setForm({ date: today(), category: "service", amount: "", currency: "CZK", note: "", car_id: "", supplier: "", invoice_number: "" }); setAttachFile(null); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!(await confirmDialog({ title: "Smazat náklad?", confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/expenses/${it.id}`, { method: "DELETE", token }); toast.success("Smazáno."); load(); }
    catch (e) { toast.error(e.message); }
  };
  const viewAttachment = (it) => openPdf(`/admin/expenses/${it.id}/attachment`, token).catch((e) => toast.error(e.message));
  const downloadAttachment = (it) => downloadFile(`/admin/expenses/${it.id}/attachment`, token, it.attachment_filename || `doklad-${it.id}`).catch((e) => toast.error(e.message));
  const downloadZip = async () => {
    setZipBusy(true);
    try { await downloadFile(`/admin/expenses/attachments/zip?month=${zipMonth}`, token, `naklady-prilohy-${zipMonth}.zip`); }
    catch (e) { toast.error(e.message); } finally { setZipBusy(false); }
  };
  const exportCsv = () => {
    if (!items.length) { toast.error("Není co exportovat."); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Datum", "Kategorie", "Dodavatel", "Č. faktury", "Vozidlo", "Původní částka", "Měna", "Kurz ČNB", "Kurz ke dni", "Částka CZK", "Poznámka"];
    const rows = items.map((it) => [
      it.date || "", it.category_label || catLabel(it.category), it.supplier || "", it.invoice_number || "",
      it.car_label || "", it.original_amount != null ? it.original_amount : it.amount, it.currency || "CZK",
      it.fx_rate || "", it.fx_date || "", it.amount, it.note || "",
    ].map(esc).join(";"));
    const csv = "\uFEFF" + [head.map(esc).join(";"), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `naklady-${today()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const pickRowFile = (id) => { pendingRowId.current = id; rowFileRef.current?.click(); };
  const onRowFile = async (e) => {
    const file = e.target.files?.[0]; const id = pendingRowId.current;
    e.target.value = ""; if (!file || !id) return;
    setUploadingId(id);
    try { await uploadFile(`/admin/expenses/${id}/attachment`, file, token); toast.success("Příloha nahrána."); load(); }
    catch (err) { toast.error(err.message); } finally { setUploadingId(null); }
  };

  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const thisMonth = monthKey(today());
  const monthTotal = items.filter((i) => monthKey(i.date) === thisMonth).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const byCat = CATS.map((c) => ({ ...c, sum: items.filter((i) => i.category === c.key).reduce((s, i) => s + (Number(i.amount) || 0), 0) })).filter((c) => c.sum > 0);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="expenses-tab">
      <input ref={rowFileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={onRowFile} data-testid="expense-row-file-input" />
      {bulkOpen && <BulkExpenseModal cars={cars} token={token} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      <div className="lg:col-span-1">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-4 inline-flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" /> Nový náklad</h3>
          <form onSubmit={add} className="space-y-3" data-testid="expense-form">
            <DateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} placeholder="Datum" testid="expense-date" />
            <select className="input-dark" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category">
              {CATS.map((c) => <option key={c.key} value={c.key} className="bg-[#0a0a0a]">{c.label}</option>)}
            </select>
            <div className="flex gap-2">
              <input type="number" step="0.01" className="input-dark flex-1" placeholder="Částka *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" />
              <select className="input-dark w-24" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="expense-currency">
                <option value="CZK" className="bg-[#0a0a0a]">Kč</option>
                <option value="EUR" className="bg-[#0a0a0a]">€ EUR</option>
                <option value="USD" className="bg-[#0a0a0a]">$ USD</option>
              </select>
            </div>
            {form.currency !== "CZK" && Number(form.amount) > 0 && rates?.rates?.[form.currency] && (
              <p className="text-[11px] text-emerald-300 -mt-1" data-testid="expense-fx-preview">≈ {fmtCZK(Math.round(Number(form.amount) * rates.rates[form.currency]))} (kurz ČNB ke dni {rates.date} · {rates.rates[form.currency]})</p>
            )}
            <input className="input-dark" placeholder="Poznámka" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="expense-note" />
            <div className="grid grid-cols-2 gap-2">
              <input className="input-dark" placeholder="Dodavatel (např. Araver)" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="expense-supplier" />
              <input className="input-dark" placeholder="Č. faktury" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="expense-invoice-number" />
            </div>
            <p className="text-[10px] text-zinc-600 -mt-1">Dodavatel + č. faktury se propíší do sekce <strong className="text-zinc-400">Faktury → Přijaté</strong>.</p>
            <select className="input-dark" value={form.car_id} onChange={(e) => setForm({ ...form, car_id: e.target.value })} data-testid="expense-car">
              <option value="" className="bg-[#0a0a0a]">Bez vozidla (volitelné)</option>
              {cars.map((c) => <option key={c.id} value={c.id} className="bg-[#0a0a0a]">{c.brand} {c.model}{c.license_plate ? ` (${c.license_plate})` : ""}</option>)}
            </select>
            <label className="block">
              <span className="text-[11px] text-zinc-500 mb-1 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Faktura / PDF / foto (volitelné)</span>
              <input type="file" accept=".pdf,image/*" onChange={(e) => setAttachFile(e.target.files?.[0] || null)} className="block w-full text-xs text-zinc-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-orange-500/15 file:text-orange-300 file:text-xs file:cursor-pointer cursor-pointer" data-testid="expense-attachment-input" />
              {attachFile && <span className="text-[11px] text-emerald-300 mt-1 block truncate">Přiloží se: {attachFile.name}</span>}
            </label>
            <button type="submit" disabled={saving} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="expense-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat náklad
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="glass rounded-2xl p-5" data-testid="expense-total">
            <div className="flex items-center gap-2 text-red-300 text-xs font-medium mb-1"><TrendingDown className="w-4 h-4" /> Náklady celkem</div>
            <p className="text-2xl font-bold font-['Cabinet_Grotesk'] text-red-300">{fmtCZK(total)}</p>
          </div>
          <div className="glass rounded-2xl p-5" data-testid="expense-month">
            <div className="flex items-center gap-2 text-zinc-400 text-xs font-medium mb-1"><Wallet className="w-4 h-4" /> Tento měsíc</div>
            <p className="text-2xl font-bold font-['Cabinet_Grotesk']">{fmtCZK(monthTotal)}</p>
          </div>
        </div>

        {byCat.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="expense-by-cat">
            {byCat.map((c) => (
              <span key={c.key} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300">{c.label}: <strong className="text-white">{fmtCZK(c.sum)}</strong></span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><Wallet className="w-5 h-5 text-orange-400" /> Náklady ({items.length})</h3>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 pl-3 pr-1 py-0.5" data-testid="expense-zip-wrap">
              <FolderArchive className="w-4 h-4 text-orange-300 shrink-0" />
              <input type="month" value={zipMonth} onChange={(e) => setZipMonth(e.target.value)} className="bg-transparent text-sm text-zinc-200 outline-none w-[7.5rem] [color-scheme:dark]" data-testid="expense-zip-month" />
              <button onClick={downloadZip} disabled={zipBusy} className="text-sm px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="expense-zip-download" title="Stáhnout všechny přílohy za vybraný měsíc jako ZIP">{zipBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Přílohy (ZIP)</button>
            </div>
            <button onClick={exportCsv} className="text-sm px-3 py-1.5 rounded-full bg-white/5 text-zinc-300 hover:bg-white/10 inline-flex items-center gap-1.5" data-testid="expense-export-csv"><Download className="w-4 h-4" /> Export CSV</button>
            <button onClick={() => setBulkOpen(true)} className="text-sm px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 inline-flex items-center gap-1.5" data-testid="expense-bulk-open"><Sparkles className="w-4 h-4" /> Hromadně z účtenek (AI)</button>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="expenses-empty">Zatím žádné náklady.</div>
        ) : (
          <div className="space-y-2" data-testid="expenses-list">
            {items.map((it) => (
              <div key={it.id} className="glass rounded-xl p-4 flex items-center gap-3" data-testid={`expense-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{it.supplier ? `${it.supplier} · ` : ""}{catLabel(it.category)}{it.note ? ` · ${it.note}` : ""}</p>
                  <p className="text-[11px] text-zinc-500">{fmtD(it.date)}{it.invoice_number ? ` · č. ${it.invoice_number}` : ""}{it.car_label ? ` · ${it.car_label}` : ""}</p>
                </div>
                <span className="text-red-300 font-medium shrink-0 text-right">
                  {fmtCZK(it.amount)}
                  {it.currency && it.currency !== "CZK" && it.original_amount != null && (
                    <span className="block text-[11px] text-zinc-500">{it.original_amount} {it.currency === "EUR" ? "€" : it.currency === "USD" ? "$" : it.currency}{it.fx_rate ? ` · kurz ${it.fx_rate}${it.fx_date ? ` (${it.fx_date})` : ""}` : ""}</span>
                  )}
                </span>
                {it.has_attachment ? (
                  <div className="flex items-center shrink-0">
                    <button onClick={() => viewAttachment(it)} className="text-orange-300 hover:text-orange-200 p-1" title={`Zobrazit přílohu: ${it.attachment_filename || ""}`} data-testid={`expense-view-attachment-${it.id}`} aria-label="Zobrazit přílohu"><FileText className="w-4 h-4" /></button>
                    <button onClick={() => downloadAttachment(it)} className="text-zinc-400 hover:text-orange-200 p-1" title={`Stáhnout přílohu: ${it.attachment_filename || ""}`} data-testid={`expense-download-attachment-${it.id}`} aria-label="Stáhnout přílohu"><Download className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => pickRowFile(it.id)} disabled={uploadingId === it.id} className="text-zinc-600 hover:text-orange-300 p-1 shrink-0 disabled:opacity-60" title="Přiložit fakturu / PDF / foto" data-testid={`expense-attach-${it.id}`} aria-label="Přiložit soubor">{uploadingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}</button>
                )}
                <button onClick={() => remove(it)} className="text-zinc-600 hover:text-red-400 p-1 shrink-0" data-testid={`expense-delete-${it.id}`} aria-label="Smazat"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpensesTab;
